import { Plane, Anchor, Info } from "lucide-react";
import { TID } from "@/data/site";

const HotelIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {/* Building outline */}
    <path d="M3.5 20.5h17" />
    <path d="M5 20.5V6.2a1 1 0 0 1 .55-.9l6-3a1 1 0 0 1 .9 0l6 3a1 1 0 0 1 .55.9V20.5" />
    {/* Star / sign on roof */}
    <path d="M12 3.2v1.6" />
    {/* Windows: 2 rows x 3 cols */}
    <rect x="7" y="9" width="2.2" height="2.2" rx="0.3" />
    <rect x="10.9" y="9" width="2.2" height="2.2" rx="0.3" />
    <rect x="14.8" y="9" width="2.2" height="2.2" rx="0.3" />
    <rect x="7" y="12.8" width="2.2" height="2.2" rx="0.3" />
    <rect x="14.8" y="12.8" width="2.2" height="2.2" rx="0.3" />
    {/* Door */}
    <path d="M10.9 20.5v-3.7a1.1 1.1 0 0 1 1.1-1.1h0a1.1 1.1 0 0 1 1.1 1.1v3.7" />
  </svg>
);

const PolicyCard = ({ icon: Icon, eyebrow, title, freeBlock, body, testId, accent = false }) => {
  return (
    <article
      data-testid={testId}
      className={`relative rounded-3xl overflow-hidden glass-strong lift ${
        accent ? "ring-1 ring-gold/30" : ""
      }`}
    >
      <div className="p-7 lg:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-gold">
              {eyebrow}
            </div>
            <h3 className="mt-2 font-display text-2xl md:text-3xl leading-tight">
              {title}
            </h3>
          </div>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/15 text-gold border border-gold/30">
            <Icon className="h-5 w-5" />
          </span>
        </div>

        <div className="mt-6 inline-flex items-baseline gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/25">
          <span className="font-display text-2xl text-gold">{freeBlock.value}</span>
          <span className="text-xs uppercase tracking-[0.16em] text-gold/85">
            {freeBlock.label}
          </span>
        </div>

        <div className="mt-6 space-y-3 text-sm text-foreground/80 leading-relaxed">
          {body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-6 ticket-edge bg-white/5 h-px" />

        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="text-[0.7rem] uppercase tracking-[0.2em] text-foreground/55">
            Extra waiting charge
          </div>
          <div className="inline-flex items-baseline gap-1.5 rounded-xl bg-white/5 border border-white/8 px-4 py-2">
            <span className="text-xs text-foreground/65">+10 min</span>
            <span className="font-display text-2xl text-foreground">€5</span>
          </div>
        </div>
      </div>
    </article>
  );
};

const WaitingPolicy = () => {
  return (
    <section id="waiting" data-testid={TID.waitingSection} className="section relative">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-gold">
              Waiting Policy
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.05]">
              We wait for you,
              <br />
              <span className="text-gold italic">stress-free.</span>
            </h2>
          </div>
          <p className="max-w-md text-sm text-foreground/70 leading-relaxed">
            Whether you are arriving on a flight, stepping off a cruise ship or
            heading out for a departure — your driver gives you generous free
            waiting time before any extra charge applies.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          <PolicyCard
            testId={TID.waitingAirport}
            icon={Plane}
            eyebrow="Airport Arrivals"
            title="One hour free waiting"
            freeBlock={{ value: "60 min", label: "Free" }}
            accent
            body={[
              "Your driver provides up to 1 hour of free waiting time, starting from your actual flight landing time.",
              "If your selected pickup time is later than the flight landing, the free hour starts from the scheduled pickup time instead.",
              "After the first hour, every additional 10 minutes is charged at €5.",
            ]}
          />
          <PolicyCard
            testId={TID.waitingPort}
            icon={Anchor}
            eyebrow="Port Arrivals"
            title="20 minutes free waiting"
            freeBlock={{ value: "20 min", label: "Free" }}
            body={[
              "For every port pickup, your driver waits up to 20 minutes from the moment your ship docks at Piraeus or any other port.",
              "If your selected pickup time is later than the ship arrival, the free 20 minutes start from the scheduled pickup time instead.",
              "After the first 20 minutes, every additional 10 minutes is charged at €5.",
            ]}
          />
          <PolicyCard
            testId={TID.waitingDeparture}
            icon={HotelIcon}
            eyebrow="All Departures"
            title="10 minutes free waiting"
            freeBlock={{ value: "10 min", label: "Free" }}
            body={[
              "For every departure transfer — to the airport, to any port or anywhere else — the driver can wait up to 10 minutes from the scheduled pickup time at no charge.",
              "After the free 10 minutes, every additional 10 minutes is charged at €5.",
            ]}
          />
        </div>

        <div className="mt-8 flex items-start gap-3 p-5 rounded-2xl glass text-sm text-foreground/75">
          <Info className="h-5 w-5 text-gold shrink-0 mt-0.5" />
          <p>
            In all three cases, the free waiting period is complimentary. Beyond
            it, the same rule applies everywhere:{" "}
            <strong className="text-foreground">every extra 10 minutes = €5</strong>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default WaitingPolicy;
